import { getUnreadNotificationCount } from '../api/notifications.client';
import { isTutorLiveSessionActive } from '../../tutor/liveSessionActivity';

type UnreadResult = Awaited<ReturnType<typeof getUnreadNotificationCount>>;

let inflightUnread: Promise<UnreadResult> | null = null;
let pollTimer: number | null = null;
let subscriberCount = 0;
const listeners = new Set<(unread: number) => void>();

export const SUPPORT_NOTIFICATIONS_POLL_MS = 30_000;

function notify(unread: number): void {
  listeners.forEach((fn) => fn(unread));
}

async function pollUnreadOnce(): Promise<void> {
  if (inflightUnread) {
    try {
      const data = await inflightUnread;
      notify(data.unread);
    } catch {
      /* ignore */
    }
    return;
  }
  inflightUnread = getUnreadNotificationCount()
    .then((data) => {
      notify(data.unread);
      return data;
    })
    .finally(() => {
      inflightUnread = null;
    });
  try {
    await inflightUnread;
  } catch {
    /* ignore */
  }
}

/** Shared unread-count poll owner (one interval for all consumers). */
export function acquireSupportNotificationsUnreadPoll(
  onUnread: (count: number) => void,
  options?: { enabled?: boolean; pauseWhileLive?: boolean },
): () => void {
  const enabled = options?.enabled !== false;
  if (!enabled) return () => {};

  listeners.add(onUnread);
  subscriberCount += 1;

  if (subscriberCount === 1) {
    void pollUnreadOnce();
    pollTimer = window.setInterval(() => {
      if (options?.pauseWhileLive && isTutorLiveSessionActive()) return;
      void pollUnreadOnce();
    }, SUPPORT_NOTIFICATIONS_POLL_MS);
  }

  return () => {
    listeners.delete(onUnread);
    subscriberCount = Math.max(0, subscriberCount - 1);
    if (subscriberCount === 0 && pollTimer != null) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
  };
}

export function __resetSupportNotificationsUnreadPollForTests(): void {
  listeners.clear();
  subscriberCount = 0;
  inflightUnread = null;
  if (pollTimer != null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}
